import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuthStore } from '@/stores/auth'
import { useColors } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'
import { CheckCircle, Clock, AlertCircle, RefreshCw, Zap, Play } from 'lucide-react'

import { Images } from '@/constants/images'

const churchsLogo = Images.churchs
const okLogo = Images.ok
const piccadillyLogo = Images.piccadilly
const kwLogo = Images.kw
const falconLogo = Images.falcon
const hardeesLogo = Images.hardees

export const Route = createFileRoute('/_authenticated/auto-reporting')({
  component: AutoReportingPage,
})

interface AutoReportingEntry {
  id: string
  company: string
  date: string
  importStatus: 'done' | 'pending' | 'failed'
  reportStatus: 'done' | 'pending' | 'failed'
}

interface CompanyConfig {
  id: string
  name: string
  apiName: string
  downloadApiName?: string
  logo?: string
  logoFit?: 'contain' | 'cover'
  logoPadding?: number
  logoPaddingDetail?: number
}

// Updated company list as specified
const companies: CompanyConfig[] = [
  {
    id: 'churchs',
    name: 'Churchs',
    apiName: 'Churchs',
    logo: churchsLogo,
    logoFit: 'cover',
    logoPadding: 0.06,
    logoPaddingDetail: 0.12,
  },
  {
    id: 'ok-holdings',
    name: 'OK Holdings',
    apiName: 'OK Holdings',
    logo: okLogo,
    logoFit: 'cover',
    logoPadding: 0.08,
    logoPaddingDetail: 0.14,
  },
  { id: 'piccadilly', name: 'Piccadilly', apiName: 'Piccadilly', logo: piccadillyLogo },
  { id: 'kw', name: 'KW', apiName: 'KW', logo: kwLogo },
  {
    id: 'nbrg',
    name: 'NBRG',
    apiName: 'NBRG',
    logo: hardeesLogo,
    logoFit: 'cover',
    logoPadding: 0.08,
    logoPaddingDetail: 0.14,
  },
  {
    id: 'corporate',
    name: 'Corporate',
    apiName: 'Corporate',
    downloadApiName: 'Corporate',
    logo: falconLogo,
    logoFit: 'cover',
    logoPadding: 0.06,
    logoPaddingDetail: 0.12,
  },
]

// Get yesterday's date in YYYY-MM-DD format
const getYesterdayDate = () => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return yesterday.toISOString().split('T')[0]
}

function AutoReportingPage() {
  const { isDarkMode } = useThemeStore()
  const Colors = useColors(isDarkMode)
  const { userToken } = useAuthStore()
  const defaultDate = useMemo(() => getYesterdayDate(), [])
  
  // PIN Lock State
  const [isLocked, setIsLocked] = useState(true)
  const [pinInput, setPinInput] = useState('')
  const [pinError, setPinError] = useState('')
  
  const CORRECT_PIN = '1590'

  const [selectedCompany, setSelectedCompany] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState(defaultDate)
  const [selectedOperation, setSelectedOperation] = useState<'Import' | 'Report' | 'both'>('both')
  const [retryingImport, setRetryingImport] = useState(false)
  const [retryingReport, setRetryingReport] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [statusData, setStatusData] = useState<Record<string, AutoReportingEntry>>({})
  const [loadingStatus, setLoadingStatus] = useState(false)

  const getCompanyInitials = (name: string) =>
    name
      .split(' ')
      .filter(Boolean)
      .map(word => word[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 3)

  const getLogoPadding = (company: CompanyConfig, variant: 'card' | 'detail') => {
    if (variant === 'detail') {
      if (company.logoPaddingDetail !== undefined) {
        return company.logoPaddingDetail
      }
      if (company.logoPadding !== undefined) {
        return company.logoPadding + 0.04
      }
      return company.logoFit === 'cover' ? 0.36 : 0.5
    }

    if (company.logoPadding !== undefined) {
      return company.logoPadding
    }
    return company.logoFit === 'cover' ? 0.25 : 0.375
  }

  const handlePinSubmit = () => {
    if (pinInput === CORRECT_PIN) {
      setIsLocked(false)
      setPinError('')
    } else {
      setPinInput('')
      setPinError('Incorrect PIN')
    }
  }

  const handlePinKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePinSubmit()
    }
  }

  const maxSelectableDate = defaultDate

  // Always use yesterday's date for summary table
  const summaryTableDate = maxSelectableDate

  // Function to check status for all companies
  const checkAllCompaniesStatus = useCallback(async (dateToCheck: string) => {
    if (!userToken) {
      console.log('[AutoReporting] No token available; skipping status check')
      return
    }

    console.log('[AutoReporting] Checking status for all companies', { dateToCheck })

    setLoadingStatus(true)
    const newStatusData: Record<string, AutoReportingEntry> = {}

    try {
      const cleanToken = userToken.toString().trim().replace(/^["']|["']$/g, '').replace(/\s+/g, '')
      
      // Helper to map API text response to our status tokens
      const parseStatus = (raw: string): 'done' | 'pending' | 'failed' => {
        const lower = raw.trim().toLowerCase()
        if (lower === 'completed' || lower === 'success') return 'done'
        if (lower === 'failed') return 'failed'
        return 'pending'
      }

      const normalizeKey = (value: string) => value.trim().toLowerCase()

      const fetchStatusesForOperation = async (
        operation: 'import' | 'report',
      ): Promise<Record<string, 'done' | 'pending' | 'failed'>> => {
        const payload = {
          Company: 'all',
          Date: dateToCheck,
          Operation: operation,
        }

        const response = await fetch(
          'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/AutoReportingStatus',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${cleanToken}`,
            },
            body: JSON.stringify(payload),
          }
        )

        if (!response.ok) {
          console.log('[AutoReporting] Bulk status request failed', {
            operation,
            status: response.status,
            statusText: response.statusText,
          })
          return {}
        }

        const rawText = await response.text()
        console.log('[AutoReporting] Bulk status raw response', {
          operation,
          result: rawText,
        })

        const statusMap: Record<string, 'done' | 'pending' | 'failed'> = {}

        try {
          const parsed = JSON.parse(rawText)
          if (Array.isArray(parsed)) {
            for (const item of parsed) {
              const companyName = (item?.company ?? item?.Company ?? '').toString()
              const statusValue = (item?.status ?? item?.Status ?? '').toString()
              if (companyName) {
                statusMap[normalizeKey(companyName)] = parseStatus(statusValue)
              }
            }
          } else if (parsed && typeof parsed === 'object') {
            for (const [key, value] of Object.entries(parsed)) {
              if (typeof value === 'string') {
                statusMap[normalizeKey(key)] = parseStatus(value)
              }
            }
          } else {
            console.log('[AutoReporting] Unexpected status payload', { operation, parsed })
          }
        } catch (parseError) {
          console.log('[AutoReporting] Bulk status JSON parse failed', { operation, parseError })
        }

        return statusMap
      }

      const importStatuses = await fetchStatusesForOperation('import')
      const reportStatuses = await fetchStatusesForOperation('report')

      const deriveAggregateStatus = (map: Record<string, 'done' | 'pending' | 'failed'>) => {
        const values = Object.values(map)
        if (values.length === 0) return 'pending'
        if (values.includes('failed')) return 'failed'
        if (values.every((status) => status === 'done')) return 'done'
        return 'pending'
      }

      // Check status for each company
      for (const company of companies) {
        const keysToCheck = [company.apiName, company.name]
        let importStatus: 'done' | 'pending' | 'failed' | undefined
        let reportStatus: 'done' | 'pending' | 'failed' | undefined

        for (const key of keysToCheck) {
          const normalized = normalizeKey(key)
          if (importStatus === undefined && normalized in importStatuses) {
            importStatus = importStatuses[normalized]
          }
          if (reportStatus === undefined && normalized in reportStatuses) {
            reportStatus = reportStatuses[normalized]
          }
        }

        if (company.apiName === 'all') {
          importStatus = deriveAggregateStatus(importStatuses)
          reportStatus = deriveAggregateStatus(reportStatuses)
        }

        const finalImportStatus = importStatus ?? 'pending'
        const finalReportStatus = reportStatus ?? 'pending'

        newStatusData[company.name] = {
          id: company.id,
          company: company.name,
          date: dateToCheck,
          importStatus: finalImportStatus,
          reportStatus: finalReportStatus,
        }
      }

      setStatusData(newStatusData)
    } catch (error) {
      console.log('[AutoReporting] Error checking status loop', error)
    } finally {
      setLoadingStatus(false)
    }
  }, [userToken])

  // Load status on component mount and when date changes
  useEffect(() => {
    console.log('[AutoReporting] useEffect initial load triggered', { summaryTableDate })
    checkAllCompaniesStatus(summaryTableDate)
  }, [checkAllCompaniesStatus, summaryTableDate])

  // Refresh status when selected date changes (for display cards)
  useEffect(() => {
    console.log('[AutoReporting] useEffect selectedDate changed', { selectedDate })
    checkAllCompaniesStatus(selectedDate)
  }, [selectedDate, checkAllCompaniesStatus])

  const getStatusIcon = (status: 'done' | 'pending' | 'failed') => {
    switch (status) {
      case 'done':
        return <CheckCircle size={16} color={Colors.success.main} />
      case 'pending':
        return <Clock size={16} color={Colors.warning.main} />
      case 'failed':
        return <AlertCircle size={16} color={Colors.error.main} />
    }
  }

  const getStatusText = (status: 'done' | 'pending' | 'failed') => {
    switch (status) {
      case 'done':
        return 'Done'
      case 'pending':
        return 'Pending'
      case 'failed':
        return 'Failed'
    }
  }

  const getStatusColor = (status: 'done' | 'pending' | 'failed') => {
    switch (status) {
      case 'done':
        return Colors.success.main
      case 'pending':
        return Colors.warning.main
      case 'failed':
        return Colors.error.main
    }
  }

  const handleProcessAutoReporting = async () => {
    if (!userToken || !selectedCompany || !selectedDate) {
      alert('Please select a company, date, and ensure you are logged in.')
      return
    }

    // Check date restriction
    const minDate = new Date('2025-08-03')
    const selectedDateObj = new Date(selectedDate)
    if (selectedDateObj < minDate) {
      alert('Please select a date from August 3, 2025 or later.')
      return
    }

    const company = companies.find(c => c.name === selectedCompany)
    if (!company) return

    setProcessing(true)

    try {
      const cleanToken = userToken.toString().trim().replace(/^["']|["']$/g, '').replace(/\s+/g, '')
      
      // Use processAutoReporting for operations
      const payload = {
        Company: company.apiName,
        Date: selectedDate,
        Operation: selectedOperation.toLowerCase()
      }

      const response = await fetch(
        'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/processAutoReporting',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cleanToken}`,
          },
          body: JSON.stringify(payload),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Processing failed: ${errorText}`)
      }

      const result = await response.text()
      alert(`Success: ${result}`)
      
      // Refresh status after processing
      setTimeout(() => {
        checkAllCompaniesStatus(selectedDate)
      }, 2000)
      
    } catch (error: any) {
      alert(`Error: ${error.message}`)
    } finally {
      setProcessing(false)
    }
  }

  const handleRetryOperation = async (operation: 'Import' | 'Report') => {
    if (!userToken || !selectedCompany) {
      alert('Please select a company and ensure you are logged in.')
      return
    }

    const confirmed = window.confirm('Are you sure you want to run the report? This will send email to everyone.')
    if (!confirmed) {
      return
    }

    const company = companies.find(c => c.name === selectedCompany)
    if (!company) return

    const setRetrying = operation === 'Import' ? setRetryingImport : setRetryingReport
    setRetrying(true)

    try {
      const cleanToken = userToken.toString().trim().replace(/^["']|["']$/g, '').replace(/\s+/g, '')
      
      const payload = {
        Company: company.apiName,
        Date: selectedDate,
        Operation: operation.toLowerCase()
      }

      const response = await fetch(
        'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/processAutoReporting',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cleanToken}`,
          },
          body: JSON.stringify(payload),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`${operation} failed: ${errorText}`)
      }

      const result = await response.text()
      alert(`${operation} Success: ${result}`)
      
      // Refresh status after retry
      setTimeout(() => {
        checkAllCompaniesStatus(selectedDate)
      }, 2000)
      
    } catch (error: any) {
      alert(`${operation} Error: ${error.message}`)
    } finally {
      setRetrying(false)
    }
  }

  const handleDownloadReport = async (
    companyName: string,
    fileType: 'ranking' | 'flash',
  ) => {
    if (!userToken) {
      alert('Please ensure you are logged in.')
      return
    }

    const company = companies.find(c => c.name === companyName)
    if (!company) return

    try {
      const cleanToken = userToken.toString().trim().replace(/^["']|["']$/g, '').replace(/\s+/g, '')
      const downloadCompanyCode = company.downloadApiName ?? company.apiName

      const payload = {
        Company: downloadCompanyCode,
        reportDate: selectedDate,
        fileType,
      }

      const response = await fetch(
        'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/AutoReportingDownload',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cleanToken}`,
          },
          body: JSON.stringify(payload),
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Download failed: ${errorText}`)
      }

      // Handle file download
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const extension = fileType === 'ranking' ? 'pdf' : 'xlsx'
      a.href = url
      a.download = `auto_report_${downloadCompanyCode.replace(/\s+/g, '_')}_${selectedDate}.${extension}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      alert(`${fileType.toUpperCase()} report downloaded successfully`)
    } catch (error: any) {
      alert(`Download Error: ${error.message}`)
    }
  }

  // Get companies with selected date for display cards
  const getDisplayCompanies = () => {
    return companies.map(company => ({
      ...company,
      data: statusData[company.name] || {
        id: company.id,
        company: company.name,
        date: selectedDate,
        importStatus: 'pending' as const,
        reportStatus: 'pending' as const,
      }
    }))
  }

  // Get companies for summary table (always yesterday's date)
  const getSummaryCompanies = () => {
    return companies.map(company => ({
      ...company,
      data: statusData[company.name] || {
        id: company.id,
        company: company.name,
        date: summaryTableDate,
        importStatus: 'pending' as const,
        reportStatus: 'pending' as const,
      }
    }))
  }

  const displayCompanies = getDisplayCompanies()
  const summaryCompanies = getSummaryCompanies()

  const selectedCompanyInfo = selectedCompany
    ? displayCompanies.find(item => item.name === selectedCompany)
    : null

  const selectedCompanyData = selectedCompanyInfo?.data ?? null

  // Show PIN lock screen if locked
  if (isLocked) {
    return (
      <div className="flex-1">
        {/* Header */}
        <div 
          className="sticky top-0 z-40 border-b px-6 py-3"
          style={{ 
            backgroundColor: Colors.background.paper,
            borderBottomColor: Colors.border.main,
          }}
        >
          <div className="flex items-center">
            <Zap size={24} color={Colors.primary.main} className="mr-3" />
            <h1 className="text-xl font-inter-semibold" style={{ color: Colors.text.primary }}>
              Auto Reporting
            </h1>
          </div>
        </div>

        {/* PIN Lock Screen */}
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] p-6">
          <div 
            className="w-full max-w-sm p-6 rounded-2xl shadow-lg text-center"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
                : '0 4px 12px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* PIN Input */}
            <div className="mb-4">
              <input
                type="password"
                value={pinInput}
                onChange={(e) => {
                  setPinInput(e.target.value.replace(/\D/g, '').slice(0, 4))
                  setPinError('')
                }}
                onKeyPress={handlePinKeyPress}
                placeholder="Enter PIN"
                maxLength={4}
                className="w-full px-4 py-3 text-center text-lg font-inter-bold border rounded-lg tracking-wide disabled:opacity-50"
                style={{ 
                  borderColor: pinError ? Colors.error.main : Colors.border.main,
                  backgroundColor: Colors.background.default,
                  color: Colors.text.primary,
                }}
              />
            </div>

            {/* Error Message */}
            {pinError && (
              <p className="text-sm font-inter-medium mb-4 text-center" style={{ color: Colors.error.main }}>
                {pinError}
              </p>
            )}

            {/* Submit Button */}
            <button
              onClick={handlePinSubmit}
              disabled={pinInput.length !== 4}
              className="w-full py-3 rounded-lg font-inter-semibold text-white disabled:opacity-50 transition-opacity"
              style={{ backgroundColor: Colors.primary.main }}
            >
              Enter
            </button>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="flex-1">
      {/* Custom Header without company/date selectors */}
      <div 
        className="sticky top-0 z-40 border-b px-6 py-3"
        style={{ 
          backgroundColor: Colors.background.paper,
          borderBottomColor: Colors.border.main,
        }}
      >
        <div className="flex items-center">
          <Zap size={24} color={Colors.primary.main} className="mr-3" />
          <h1 className="text-xl font-inter-semibold" style={{ color: Colors.text.primary }}>
            Auto Reporting
          </h1>
        </div>
      </div>

      <div className="p-6">
        {/* Filter Tabs */}
        <div 
          className="p-4 rounded-xl mb-6"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div className="grid grid-cols-4 gap-4 items-end">
            {loadingStatus && (
              <div className="col-span-4 mb-4">
                <div className="flex items-center space-x-2 p-3 rounded-md" style={{ backgroundColor: Colors.neutral[100] }}>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: Colors.primary.main }}></div>
                  <span className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                    Loading status for all companies...
                  </span>
                </div>
              </div>
            )}
            
            {/* Date Selection */}
            <div>
              <label className="block text-sm font-inter-medium mb-2" style={{ color: Colors.text.primary }}>
                Date:
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  const value = e.target.value
                  if (!value) {
                    setSelectedDate(maxSelectableDate)
                    return
                  }
                  if (value > maxSelectableDate) {
                    setSelectedDate(maxSelectableDate)
                    return
                  }
                  setSelectedDate(value)
                }}
                max={maxSelectableDate}
                className="w-full px-3 py-2 border rounded-md font-inter-regular"
                style={{ 
                  borderColor: Colors.border.main,
                  backgroundColor: Colors.background.default,
                  color: Colors.text.primary,
                }}
              />
            </div>

            {/* Company Selection */}
            <div>
              <label className="block text-sm font-inter-medium mb-2" style={{ color: Colors.text.primary }}>
                Company:
              </label>
              <select
                value={selectedCompany || ''}
                onChange={(e) => setSelectedCompany(e.target.value || null)}
                className="w-full px-3 py-2 border rounded-md font-inter-regular"
                style={{ 
                  borderColor: Colors.border.main,
                  backgroundColor: Colors.background.default,
                  color: Colors.text.primary,
                }}
              >
                <option value="">Select Company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Operation Selection */}
            <div>
              <label className="block text-sm font-inter-medium mb-2" style={{ color: Colors.text.primary }}>
                Operation:
              </label>
              <select
                value={selectedOperation}
                onChange={(e) => setSelectedOperation(e.target.value as 'Import' | 'Report' | 'both')}
                className="w-full px-3 py-2 border rounded-md font-inter-regular"
                style={{ 
                  borderColor: Colors.border.main,
                  backgroundColor: Colors.background.default,
                  color: Colors.text.primary,
                }}
              >
                <option value="both">Both</option>
                <option value="Import">Import</option>
                <option value="Report">Report</option>
              </select>
            </div>

            {/* Process Button */}
            <div>
              <button
                onClick={handleProcessAutoReporting}
                disabled={processing || !selectedCompany || !selectedDate}
                className="flex items-center space-x-2 px-6 py-2 rounded-md font-inter-semibold text-white disabled:opacity-50 w-full justify-center"
                style={{ backgroundColor: Colors.primary.main }}
              >
                {processing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Play size={16} />
                    <span>Process</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Company Selection Cards */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
              Companies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {displayCompanies.map((company) => {
                const isSelected = selectedCompany === company.name
                const canDownload =
                  company.data.importStatus === 'done' && company.data.reportStatus === 'done'

                return (
                  <div
                    key={company.id}
                    onClick={() => setSelectedCompany(company.name)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected ? 'ring-2' : ''
                    }`}
                    style={{
                      backgroundColor: Colors.background.paper,
                      borderColor: isSelected ? Colors.primary.main : Colors.border.main,
                      boxShadow: isDarkMode 
                        ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                        : '0 2px 4px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    {/* Company Logo/Initial */}
                    <div className="flex items-center mb-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center mr-3 overflow-hidden"
                        style={{
                          backgroundColor: company.logo ? Colors.background.default : Colors.primary.main + '20',
                        }}
                      >
                        {company.logo ? (
                          <img
                            src={company.logo}
                            alt={`${company.name} logo`}
                            className="w-full h-full pointer-events-none"
                            style={{
                              objectFit: company.logoFit ?? 'contain',
                              padding: `${getLogoPadding(company, 'card')}rem`,
                            }}
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-sm font-inter-bold" style={{ color: Colors.primary.main }}>
                            {getCompanyInitials(company.name)}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>
                          {company.name}
                        </h3>
                      </div>
                    </div>

                    {/* Import Status */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-inter-medium" style={{ color: Colors.text.secondary }}>
                          Import
                        </span>
                        <div className="flex items-center space-x-1">
                          {getStatusIcon(company.data.importStatus)}
                          <span 
                            className="text-xs font-inter-medium"
                            style={{ color: getStatusColor(company.data.importStatus) }}
                          >
                            {getStatusText(company.data.importStatus)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Report Status */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-inter-medium" style={{ color: Colors.text.secondary }}>
                          Report
                        </span>
                        <div className="flex items-center space-x-1">
                          {getStatusIcon(company.data.reportStatus)}
                          <span 
                            className="text-xs font-inter-medium"
                            style={{ color: getStatusColor(company.data.reportStatus) }}
                          >
                            {getStatusText(company.data.reportStatus)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="pt-2 border-t" style={{ borderTopColor: Colors.border.main }}>
                      <p className="text-xs font-inter-regular" style={{ color: Colors.text.secondary }}>
                        Date: {company.data.date}
                      </p>
                    </div>

                    {/* Download Buttons */}
                    <div className="mt-3 flex space-x-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleDownloadReport(company.name, 'ranking')
                        }}
                        disabled={!canDownload}
                        className="flex-1 px-3 py-2 rounded-md text-xs font-inter-semibold text-white transition-opacity"
                        style={{
                          backgroundColor: canDownload ? Colors.primary.main : Colors.neutral[400],
                          opacity: canDownload ? 1 : 0.5,
                        }}
                      >
                        Download PDF
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleDownloadReport(company.name, 'flash')
                        }}
                        disabled={!canDownload}
                        className="flex-1 px-3 py-2 rounded-md text-xs font-inter-semibold text-white transition-opacity"
                        style={{
                          backgroundColor: canDownload ? Colors.success.main : Colors.neutral[400],
                          opacity: canDownload ? 1 : 0.5,
                        }}
                      >
                        Download Excel
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Company Details Panel */}
          <div className="lg:col-span-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-inter-bold" style={{ color: Colors.text.primary }}>
                {selectedCompanyData ? selectedCompany : 'Select Company'}
              </h2>
            </div>

            {selectedCompanyData && selectedCompanyInfo ? (
              <div 
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: Colors.background.paper,
                  boxShadow: isDarkMode 
                    ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                    : '0 2px 4px rgba(0, 0, 0, 0.05)',
                }}
              >
                {/* Company Logo/Initial */}
                <div className="flex items-center mb-6">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mr-4 overflow-hidden"
                    style={{
                      backgroundColor: selectedCompanyInfo?.logo
                        ? Colors.background.default
                        : Colors.primary.main + '20',
                    }}
                  >
                    {selectedCompanyInfo?.logo ? (
                      <img
                        src={selectedCompanyInfo.logo}
                        alt={`${selectedCompany} logo`}
                        className="w-full h-full"
                        style={{
                          objectFit: selectedCompanyInfo.logoFit ?? 'contain',
                          padding: `${getLogoPadding(selectedCompanyInfo, 'detail')}rem`,
                        }}
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-lg font-inter-bold" style={{ color: Colors.primary.main }}>
                        {selectedCompany ? getCompanyInitials(selectedCompany) : ''}
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-inter-bold" style={{ color: Colors.text.primary }}>
                      {selectedCompany}
                    </h3>
                  </div>
                </div>

                {/* Import Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                      Import
                    </h4>
                    <button
                      onClick={() => handleRetryOperation('Import')}
                      disabled={retryingImport}
                      className="flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-inter-medium text-white disabled:opacity-50"
                      style={{ backgroundColor: Colors.primary.main }}
                    >
                      <RefreshCw size={14} className={retryingImport ? 'animate-spin' : ''} />
                      <span>Retry</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    {getStatusIcon(selectedCompanyData.importStatus)}
                    <span 
                      className="font-inter-medium"
                      style={{ color: getStatusColor(selectedCompanyData.importStatus) }}
                    >
                      {getStatusText(selectedCompanyData.importStatus)}
                    </span>
                  </div>
                </div>

                {/* Report Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                      Report
                    </h4>
                    <button
                      onClick={() => handleRetryOperation('Report')}
                      disabled={retryingReport}
                      className="flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-inter-medium text-white disabled:opacity-50"
                      style={{ backgroundColor: Colors.primary.main }}
                    >
                      <RefreshCw size={14} className={retryingReport ? 'animate-spin' : ''} />
                      <span>Retry</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    {getStatusIcon(selectedCompanyData.reportStatus)}
                    <span 
                      className="font-inter-medium"
                      style={{ color: getStatusColor(selectedCompanyData.reportStatus) }}
                    >
                      {getStatusText(selectedCompanyData.reportStatus)}
                    </span>
                  </div>
                </div>

                {/* Date Section */}
                <div className="pt-4 border-t" style={{ borderTopColor: Colors.border.main }}>
                  <p className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                    Date: {selectedCompanyData.date}
                  </p>
                </div>

                {/* Download Buttons */}
                <div className="mt-4 flex space-x-3">
                  <button
                    type="button"
                    onClick={() => selectedCompany && handleDownloadReport(selectedCompany, 'ranking')}
                    disabled={
                      !selectedCompany ||
                      selectedCompanyData.importStatus !== 'done' ||
                      selectedCompanyData.reportStatus !== 'done'
                    }
                    className="flex-1 px-4 py-3 rounded-md text-sm font-inter-semibold text-white transition-opacity"
                    style={{
                      backgroundColor: (selectedCompanyData.importStatus === 'done' && selectedCompanyData.reportStatus === 'done') 
                        ? Colors.primary.main 
                        : Colors.neutral[400],
                      opacity: (selectedCompanyData.importStatus === 'done' && selectedCompanyData.reportStatus === 'done') ? 1 : 0.5,
                    }}
                  >
                    Download PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => selectedCompany && handleDownloadReport(selectedCompany, 'flash')}
                    disabled={
                      !selectedCompany ||
                      selectedCompanyData.importStatus !== 'done' ||
                      selectedCompanyData.reportStatus !== 'done'
                    }
                    className="flex-1 px-4 py-3 rounded-md text-sm font-inter-semibold text-white transition-opacity"
                    style={{
                      backgroundColor: (selectedCompanyData.importStatus === 'done' && selectedCompanyData.reportStatus === 'done') 
                        ? Colors.success.main 
                        : Colors.neutral[400],
                      opacity: (selectedCompanyData.importStatus === 'done' && selectedCompanyData.reportStatus === 'done') ? 1 : 0.5,
                    }}
                  >
                    Download Excel
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="p-6 rounded-xl text-center"
                style={{
                  backgroundColor: Colors.background.paper,
                  boxShadow: isDarkMode 
                    ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                    : '0 2px 4px rgba(0, 0, 0, 0.05)',
                }}
              >
                <p className="font-inter-regular" style={{ color: Colors.text.secondary }}>
                  Select a company to view details and trigger operations
                </p>
                {loadingStatus && (
                  <div className="flex items-center justify-center space-x-2 mt-4">
                    <span className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                      Loading status...
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Summary Table - Always shows yesterday's date */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-inter-bold" style={{ color: Colors.text.primary }}>
              Summary (Yesterday: {summaryTableDate})
            </h2>
            <button
              onClick={() => {
                console.log('[AutoReporting] Refresh button pressed')
                checkAllCompaniesStatus(summaryTableDate)
              }}
              disabled={loadingStatus}
              className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-inter-medium disabled:opacity-50"
              style={{ backgroundColor: Colors.primary.main, color: '#FFFFFF' }}
            >
              <RefreshCw size={14} className={loadingStatus ? 'animate-spin' : ''} />
              <span>Refresh Status</span>
            </button>
          </div>
          <div 
            className="rounded-xl overflow-hidden"
            style={{
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr 
                    className="border-b"
                    style={{ 
                      backgroundColor: isDarkMode ? Colors.background.paper : Colors.neutral[100],
                      borderBottomColor: Colors.border.main 
                    }}
                  >
                    <th className="text-left p-4 font-inter-semibold" style={{ color: Colors.text.primary }}>
                      Company
                    </th>
                    <th className="text-left p-4 font-inter-semibold" style={{ color: Colors.text.primary }}>
                      Date
                    </th>
                    <th className="text-left p-4 font-inter-semibold" style={{ color: Colors.text.primary }}>
                      Import Status
                    </th>
                    <th className="text-left p-4 font-inter-semibold" style={{ color: Colors.text.primary }}>
                      Report Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summaryCompanies.map((company) => (
                    <tr 
                      key={company.id} 
                      className="border-b hover:bg-opacity-50 transition-colors cursor-pointer"
                      style={{ borderBottomColor: Colors.border.main }}
                      onClick={() => setSelectedCompany(company.name)}
                    >
                      <td className="p-4 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {company.name}
                      </td>
                      <td className="p-4 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {company.data.date}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(company.data.importStatus)}
                          <span 
                            className="font-inter-medium text-sm"
                            style={{ color: getStatusColor(company.data.importStatus) }}
                          >
                            {getStatusText(company.data.importStatus)}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(company.data.reportStatus)}
                          <span 
                            className="font-inter-medium text-sm"
                            style={{ color: getStatusColor(company.data.reportStatus) }}
                          >
                            {getStatusText(company.data.reportStatus)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
